import React from "react";
import { createPortal } from "react-dom";
import { posAPI } from "../../services/api";
import { useI18n } from "../../i18n";
import PinPad from "./PinPad";

/**
 * "Price too low": the cashier's discount would sell something for less than
 * the shop paid for it. The sale waits here until an owner types their PIN —
 * which approves this one sale only — or the cashier goes back and changes
 * the discount.
 *
 * Says which product, never its cost: the person holding the till is often
 * not someone the owner tells what they pay suppliers.
 *
 * Rendered into document.body, like the till lock, so the register header's
 * backdrop filter cannot become its containing block.
 */
const OwnerApproval = ({ clientSaleId, products, onApproved, onCancel }) => {
  const { t } = useI18n();
  const names = products.map((product) => product.product_name).join(", ");

  const approve = async (pin) => {
    try {
      const { data } = await posAPI.approveOverride(pin, clientSaleId);
      onApproved(data.override_token);
      return null;
    } catch (err) {
      if (err.response?.data?.code === "OVERRIDE_NOT_PERMITTED") {
        return t("pos.belowCost.ownerOnly");
      }
      return err.response?.status === 401
        ? t("pos.lock.notRecognised")
        : t("pos.lock.checkFailed");
    }
  };

  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={t("pos.belowCost.title")}
      className="fixed inset-0 z-[70] bg-slate-950/95 text-slate-100 overflow-y-auto overscroll-contain"
    >
      <div className="min-h-full flex flex-col items-center justify-center px-6 py-12 pb-[calc(2.5rem+env(safe-area-inset-bottom))]">
        <span className="text-4xl leading-none" aria-hidden="true">
          ⚠️
        </span>
        <p className="mt-3 text-lg font-bold text-white text-center">
          {t("pos.belowCost.title")}
        </p>
        <p className="mt-2 max-w-sm text-sm text-slate-300 text-center">
          {t("pos.belowCost.message", { names })}
        </p>
        <p className="mt-4 text-sm font-semibold text-amber-300 text-center">
          {t("pos.belowCost.askOwner")}
        </p>

        <PinPad onSubmit={approve} />

        <button
          type="button"
          onClick={onCancel}
          className="mt-8 h-12 px-6 rounded-xl bg-slate-800 border border-slate-700 font-semibold text-slate-200 hover:bg-slate-700"
        >
          {t("pos.belowCost.changeDiscount")}
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default OwnerApproval;
